<script>
import { authState } from "@app/store/authStore.svelte.js";
import {
    defaultWatermarkSettings,
    formatWatermarkDateTime,
    normalizeWatermarkSettings,
    saveTeamWatermark,
    watermarkState,
    WATERMARK_SOURCE_REMOTE,
} from "@app/store/watermarkStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { onDestroy } from "svelte";
import WatermarkOverlay from "@app/components/player/WatermarkOverlay.svelte";
import * as m from "@app/paraglide/messages.js";

const SAMPLE_IP = "203.0.113.10";
const SAMPLE_EMAIL = "user@example.com";

let settings = $state(
    normalizeWatermarkSettings(
        authState.team?.watermark || defaultWatermarkSettings(),
    ),
);

let saving = $state(false);
let now = $state(formatWatermarkDateTime());

let canEdit = $derived(authState.user?.role === "admin");

let lastUpdated = $derived.by(() => {
    const updatedAt = authState.team?.watermark?.updatedAt;
    if (!updatedAt) return "";
    const parsed = new Date(updatedAt);
    return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleString();
});

/* Preview uses the signed in admin's own identity where available so the
   result matches what their team will see. */
let previewLines = $derived.by(() => {
    const identity = [];

    if (settings.showDateTime) identity.push(now);
    if (settings.showEmail) {
        identity.push(authState.user?.email || SAMPLE_EMAIL);
    }
    if (settings.showIpAddress) {
        identity.push(authState.ipAddress || SAMPLE_IP);
    }

    const lines = identity.length > 0 ? [identity.join(" ")] : [];

    if (settings.showCustomText && settings.customText.trim()) {
        lines.push(settings.customText.trim());
    }

    return lines;
});

const clock = setInterval(() => {
    now = formatWatermarkDateTime();
}, 1000);

onDestroy(() => {
    clearInterval(clock);
});

async function save() {
    saving = true;
    try {
        await saveTeamWatermark(settings);
        toast.push(m.options_watermark_saved_toast(), {
            classes: ["toast-success"],
        });
    } catch (err) {
        console.error("Error saving watermark settings:", err);
        toast.push(
            m.options_watermark_save_failed_toast({ error: err.message }),
            { classes: ["toast-danger"] },
        );
    } finally {
        saving = false;
    }
}
</script>

<div id="watermarkSettingsForm" aria-labelledby="watermarkSettingsTitle">
    <h5 id="watermarkSettingsTitle">{m.options_watermark_title()}</h5>
    <p class="small text-muted">{m.options_watermark_desc()}</p>

    {#if !canEdit}
        <div class="alert alert-warning py-2">
            {m.options_watermark_admin_only()}
        </div>
    {/if}

    {#if watermarkState.source === WATERMARK_SOURCE_REMOTE}
        <div class="alert alert-info py-2">
            {m.options_watermark_remote_notice()}
        </div>
    {/if}

    <div class="row">
        <div class="col-md-6">
            <h6 class="mb-2">{m.options_watermark_preview_title()}</h6>
            <div class="watermark-preview">
                <WatermarkOverlay
                    lines={settings.enabled ? previewLines : []}
                    alignment={settings.alignment}
                    textOpacity={settings.textOpacity}
                    shadowOpacity={settings.shadowOpacity} />
            </div>
            <div class="form-text">{m.options_watermark_sample_preview()}</div>
        </div>

        <div class="col-md-6">
            <h6 class="mb-2">{m.options_watermark_settings_title()}</h6>
            <div class="form-check form-switch mb-3">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="watermarkEnabled"
                    disabled={!canEdit}
                    bind:checked={settings.enabled} />
                <label class="form-check-label" for="watermarkEnabled">
                    {m.options_watermark_enable()}
                </label>
            </div>

            <h6 class="mb-2">{m.options_watermark_content_title()}</h6>
            <div class="form-check form-switch mb-2">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="watermarkDateTime"
                    disabled={!canEdit || !settings.enabled}
                    bind:checked={settings.showDateTime} />
                <label class="form-check-label" for="watermarkDateTime">
                    {m.options_watermark_date_time()}
                </label>
            </div>
            <div class="form-check form-switch mb-2">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="watermarkEmail"
                    disabled={!canEdit || !settings.enabled}
                    bind:checked={settings.showEmail} />
                <label class="form-check-label" for="watermarkEmail">
                    {m.options_watermark_email()}
                </label>
            </div>
            <div class="form-check form-switch mb-2">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="watermarkIpAddress"
                    disabled={!canEdit || !settings.enabled}
                    bind:checked={settings.showIpAddress} />
                <label class="form-check-label" for="watermarkIpAddress">
                    {m.options_watermark_ip_address()}
                </label>
            </div>
            <div class="form-check form-switch mb-2">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="watermarkCustomTextToggle"
                    disabled={!canEdit || !settings.enabled}
                    bind:checked={settings.showCustomText} />
                <label class="form-check-label" for="watermarkCustomTextToggle">
                    {m.options_watermark_custom_text()}
                </label>
            </div>
            <div class="mb-4">
                <label class="visually-hidden" for="watermarkCustomText">
                    {m.options_watermark_custom_text()}
                </label>
                <textarea
                    class="form-control"
                    id="watermarkCustomText"
                    rows="3"
                    maxlength="120"
                    placeholder={m.options_watermark_custom_text_placeholder()}
                    disabled={!canEdit ||
                        !settings.enabled ||
                        !settings.showCustomText}
                    bind:value={settings.customText}></textarea>
            </div>

            <h6 class="mb-2">{m.options_watermark_appearance_title()}</h6>
            <fieldset class="mb-3" disabled={!canEdit || !settings.enabled}>
                <legend class="form-label float-none fs-6 me-3 d-inline">
                    {m.options_watermark_alignment()}
                </legend>
                {#each ["top", "center", "bottom"] as alignment (alignment)}
                    <div class="form-check form-check-inline">
                        <input
                            class="form-check-input"
                            type="radio"
                            name="watermarkAlignment"
                            id="watermarkAlignment-{alignment}"
                            value={alignment}
                            bind:group={settings.alignment} />
                        <label
                            class="form-check-label"
                            for="watermarkAlignment-{alignment}">
                            {alignment === "top"
                                ? m.options_watermark_alignment_top()
                                : alignment === "center"
                                  ? m.options_watermark_alignment_center()
                                  : m.options_watermark_alignment_bottom()}
                        </label>
                    </div>
                {/each}
            </fieldset>

            <div class="mb-3">
                <label class="form-label" for="watermarkTextOpacity">
                    {m.options_watermark_text_opacity()}
                </label>
                <div class="d-flex align-items-center gap-2">
                    <input
                        type="range"
                        class="form-range"
                        id="watermarkTextOpacity"
                        min="0"
                        max="100"
                        step="1"
                        disabled={!canEdit || !settings.enabled}
                        bind:value={settings.textOpacity} />
                    <span class="text-muted small text-nowrap"
                        >{settings.textOpacity}%</span>
                </div>
            </div>

            <div class="mb-4">
                <label class="form-label" for="watermarkShadowOpacity">
                    {m.options_watermark_shadow_opacity()}
                </label>
                <div class="d-flex align-items-center gap-2">
                    <input
                        type="range"
                        class="form-range"
                        id="watermarkShadowOpacity"
                        min="0"
                        max="100"
                        step="1"
                        disabled={!canEdit || !settings.enabled}
                        bind:value={settings.shadowOpacity} />
                    <span class="text-muted small text-nowrap"
                        >{settings.shadowOpacity}%</span>
                </div>
            </div>

            <div class="d-flex align-items-center justify-content-between">
                {#if lastUpdated}
                    <span class="text-muted small">
                        {m.options_watermark_last_updated({ date: lastUpdated })}
                    </span>
                {:else}
                    <span></span>
                {/if}
                <button
                    type="button"
                    class="btn btn-primary"
                    disabled={!canEdit || saving}
                    onclick={save}>
                    {#if saving}
                        <span
                            class="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"></span>
                    {/if}
                    {m.options_watermark_update()}
                </button>
            </div>
        </div>
    </div>
</div>

<style>
.watermark-preview {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 0.375rem;
    overflow: hidden;
    background: linear-gradient(135deg, #3a4657 0%, #101722 55%, #05070b 100%);
}
</style>
